import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConfig } from 'hooks/use-config';
import { useGlobalQuery } from 'state/query-client/hooks';
import { scheduledJobsService } from '../services/scheduled-jobs.service';
import type { ScheduledJobItem } from '../services/scheduled-jobs.types';

/** Routine ids the API creates today are 32 hex characters; anything odd is never requested. */
const JOB_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/** A slow link gives up with the error notice so the list stays usable. */
export const LINKED_JOB_TIMEOUT_MS = 10_000;

export type LinkedScheduledJobStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

export function linkedScheduledJobKey(jobId: string | null) {
  return ['scheduled-jobs', 'linked', jobId];
}

/**
 * Reads `/scheduler?job=<id>`, loads that routine once and removes `job` from the
 * address (history replace) so Back and refresh don't reopen it. The id is kept
 * in state, so removing the parameter doesn't drop the loaded routine.
 */
export function useLinkedScheduledJob() {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const jobParam = searchParams.get('job');
  const [jobId, setJobId] = useState<string | null>(jobParam);

  useEffect(() => {
    if (jobParam === null) return;
    setJobId(jobParam);
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete('job');
        return next;
      },
      { replace: true }
    );
  }, [jobParam, setSearchParams]);

  const isValidId = !!jobId && JOB_ID_PATTERN.test(jobId);

  const query = useGlobalQuery<ScheduledJobItem | null>({
    queryKey: linkedScheduledJobKey(jobId),
    queryFn: ({ signal }) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), LINKED_JOB_TIMEOUT_MS);
      signal?.addEventListener('abort', () => controller.abort());
      return scheduledJobsService
        .getScheduledJob(jobId ?? '', blocksKey, controller.signal)
        .finally(() => clearTimeout(timeoutId));
    },
    enabled: isValidId && !!blocksKey,
    retry: false,
    staleTime: 0,
  });

  let status: LinkedScheduledJobStatus = 'idle';
  if (jobId && !isValidId) status = 'not_found';
  else if (isValidId && query.isError) status = 'error';
  else if (isValidId && query.data) status = 'found';
  else if (isValidId && query.data === null) status = 'not_found';
  else if (isValidId) status = 'loading';

  return {
    jobId,
    job: query.data ?? null,
    status,
    /** Forget the link (dialog closed); no further requests for it. */
    clear: () => setJobId(null),
  };
}
